import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import ReactSelect, { components as ReactSelectComponents, } from 'react-select';
import { fontSizes, defaultFontFamily, colors, } from '@modernhealth/vital-foundation';
import clsx from 'clsx';
import { Badge } from '../Badge/Badge';
import { Checkbox } from '../DeprecatedCheckbox/Checkbox';
import { ColorVariant } from './ColorVariant';
import { InputField } from './Field';
import * as styles from './Select.module.scss';
/**
 * The default styling for the "control" component of `<Select>`
 *
 * @param {ColorVariant} variant the color to render the borders in.
 */
const dropdownStyles = (variant) => {
    let borderColor = '';
    switch (variant) {
        case ColorVariant.Error:
            borderColor = colors.red55;
            break;
        case ColorVariant.Active:
            borderColor = colors.violet55;
            break;
        default:
            borderColor = colors.gray10;
    }
    return {
        width: '100%',
        minHeight: '52px',
        lineHeight: '24px',
        borderStyle: 'solid',
        borderWidth: '2px',
        borderColor,
        cursor: 'pointer',
        fontFamily: defaultFontFamily,
    };
};
/**
 * Wraps any "custom" `<Option>` implementations to ensure accessibility and
 * a consistent integration style.
 */
const CustomOption = ({ data, label, children, ...props }) => {
    return (_jsx(ReactSelectComponents.Option, { label: label, data: data, ...props, children: children }));
};
/**
 * Wraps any "custom" `<SingleValue>` implementations to ensure accessibility and
 * a consistent integration style.
 */
const CustomValue = ({ data, children, ...props }) => {
    return (_jsx(ReactSelectComponents.SingleValue, { data: data, ...props, children: children }));
};
/**
 * Inject a testing ID into the container for compatibility, react-select
 * typically uses DOM IDs.
 */
const TestableSelectContainer = (props) => {
    return (_jsx(ReactSelectComponents.SelectContainer, { ...props, innerProps: {
            ...props.innerProps,
            // @ts-expect-error We are injecting a new innerProp
            'data-testid': props.selectProps['data-testid'],
            'data-component': 'Select',
        } }));
};
function useMappedValues({ options, value, defaultValue, }) {
    // We allow passing in `option.value` for value and defaultValue,
    // so memoize the "found" options
    const mappedDefaultValue = useMemo(() => {
        return options?.find(option => option.value === defaultValue);
    }, [options, defaultValue]);
    const mappedValue = useMemo(() => {
        return options?.find(option => option.value === value);
    }, [options, value]);
    return {
        options,
        // eslint-disable-next-line no-nested-ternary
        ...(mappedValue !== undefined
            ? {
                value: mappedValue,
            }
            : mappedDefaultValue !== undefined
                ? {
                    defaultValue: mappedDefaultValue,
                }
                : {
                    defaultValue: null,
                }),
    };
}
export const BaseSelect = ({ placeholder, options, optionComponent, valueComponent, components = {}, ...props }) => {
    return (_jsx(ReactSelect, { placeholder: placeholder, options: options, instanceId: props.id ? `${props.id}-child` : undefined, ...props, components: {
            SelectContainer: TestableSelectContainer,
            ...(optionComponent ? { Option: optionComponent } : {}),
            ...(valueComponent ? { SingleValue: valueComponent } : {}),
            ...components,
        } }));
};
const baseOptionStyle = {
    paddingTop: '4px',
    color: colors.gray85,
    fontSize: `${fontSizes.medium}px`,
    cursor: 'pointer',
    fontFamily: defaultFontFamily,
};
const dsSelectTheme = {
    colors: {
        primary: colors.violet55,
        danger: colors.red55,
    },
};
function buildSelectStyles(colorVariant, containerStyle, selectStyle, optionStyle) {
    return {
        menu: (base) => {
            return {
                ...base,
                // Duet Date Picker is 600
                zIndex: 500,
            };
        },
        container: (base, containerProps) => {
            return { ...base, flex: 1, ...containerStyle?.(containerProps) };
        },
        option: (base, optionProps) => {
            return {
                ...base,
                ...defaultOptionStyle(optionProps),
                ...optionStyle?.(optionProps),
            };
        },
        control: (base, controlProps) => {
            return {
                ...base,
                ...dropdownStyles(colorVariant),
                ...selectStyle?.(controlProps),
            };
        },
        singleValue: (base) => {
            return {
                ...base,
                fontSize: `${fontSizes.medium}px`,
            };
        },
        menuPortal: (base) => {
            return {
                ...base,
                /** we would want to move z-index presets to variables */
                zIndex: 900,
            };
        },
    };
}
function buildTheme(theme) {
    return {
        ...theme,
        ...dsSelectTheme,
        colors: {
            ...theme.colors,
            ...dsSelectTheme.colors,
        },
    };
}
function useSelectStyle({ colorVariant, containerStyle, selectStyle, optionStyle, }) {
    return {
        theme: buildTheme,
        styles: buildSelectStyles(colorVariant, containerStyle, selectStyle, optionStyle),
    };
}
function defineCustomSelectComponents({ optionComponent, valueComponent, }) {
    const BaseOptionComponent = optionComponent;
    const BaseValueComponent = valueComponent;
    const WrappedOptionComponent = ({ data, ...props }) => {
        return (_jsx(CustomOption, { data: data, ...props, children: _jsx(BaseOptionComponent, { option: data }) }));
    };
    const WrappedValueComponent = ({ data, ...props }) => {
        return (_jsx(CustomValue, { data: data, ...props, children: BaseValueComponent ? (_jsx(BaseValueComponent, { option: data })) : (_jsx(ReactSelectComponents.SingleValue, { data: data, ...props })) }));
    };
    return {
        optionComponent: WrappedOptionComponent,
        valueComponent: WrappedValueComponent,
    };
}
const defaultOptionStyle = (props) => {
    if (props.isSelected || props.isFocused) {
        return {
            ...baseOptionStyle,
            backgroundColor: colors.gray05,
        };
    }
    return baseOptionStyle;
};
function useSingleSelect({ selectStyle = undefined, optionStyle = undefined, containerStyle = undefined, colorVariant = ColorVariant.Default, label, labelledBy, id, value, defaultValue, options, ...props }) {
    const mappedValues = useMappedValues({ value, defaultValue, options });
    const selectProps = useSelectStyle({
        colorVariant,
        containerStyle,
        selectStyle,
        optionStyle,
    });
    return {
        ...props,
        id,
        'aria-label': label,
        'aria-labelledby': labelledBy,
        ...selectProps,
        ...mappedValues,
    };
}
/**
 * A simple, extensible Select component.
 */
const Select = (props) => {
    const selectDefinition = useSingleSelect(props);
    return _jsx(BaseSelect, { ...selectDefinition });
};
function withCustomOption(optionComponent, valueComponent) {
    const components = defineCustomSelectComponents({
        optionComponent,
        valueComponent,
    });
    return props => {
        const selectDefinition = useSingleSelect(props);
        return _jsx(BaseSelect, { ...components, ...selectDefinition });
    };
}
Select.withCustomOption = withCustomOption;
function useSingleSelectField({ fieldId, label, labelledBy, error, useDefaultPlaceholder, ...selectProps }, components) {
    return {
        fieldId,
        label,
        labelledBy,
        error,
        value: selectProps.value,
        initialValue: selectProps.defaultValue,
        // react-select's dropdown icon is 20px wide with 8px padding on all sides.
        rightPadding: '36px',
        useDefaultPlaceholder,
        renderInput: state => {
            const { onFocus, onUnfocus, setIsFilled, labelId, helpId, inputId, variant, } = state;
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const selectDefinition = useSingleSelect({
                ...selectProps,
                id: inputId,
                labelledBy: labelId,
                colorVariant: variant,
            });
            return (_jsx(BaseSelect, { ...selectDefinition, ...components, onMenuClose: onUnfocus, onMenuOpen: onFocus, "aria-describedby": helpId, onChange: option => {
                    selectProps.onChange?.(option);
                    setIsFilled(option != null);
                } }));
        },
    };
}
/**
 * A form field containing a label and a `<Select>` component.
 * Supports all of the properties from `<Select>` and `<InputField>`
 */
const SelectField = (props) => {
    const inputFieldProps = useSingleSelectField(props);
    return _jsx(InputField, { ...inputFieldProps });
};
function fieldWithCustomOption(optionComponent, valueComponent) {
    const components = defineCustomSelectComponents({
        optionComponent,
        valueComponent,
    });
    return props => {
        const inputFieldProps = useSingleSelectField(props, components);
        return _jsx(InputField, { ...inputFieldProps });
    };
}
SelectField.withCustomOption = fieldWithCustomOption;
const MultiSelectOption = ({ data, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
children, ...props }) => {
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
    _jsx("div", { onClick: e => {
            e.preventDefault();
            e.stopPropagation();
        }, children: _jsx(ReactSelectComponents.Option, { data: data, ...props, isSelected: false, className: styles.multiSelectOption, children: _jsx(Checkbox, { label: data.label, value: props.isSelected }) }) }));
};
const MultiSelectValueContainer = (props) => {
    return (_jsx(ReactSelectComponents.ValueContainer, { ...props, className: clsx(props.hasValue && styles.multiSelectValueContainerWithValue) }));
};
const MultiSelectValue = ({ data, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
children, ...props }) => {
    return (_jsx("div", { className: styles.multiSelectValue, children: _jsx(Badge, { title: data.label, 
            // @ts-expect-error onClick is typed for button event as parameter
            onDismiss: props.removeProps.onClick, size: "medium" }) }));
};
function useMultiSelect({ selectStyle = undefined, optionStyle = undefined, containerStyle = undefined, colorVariant = ColorVariant.Default, label, labelledBy, id, ...props }) {
    const selectProps = useSelectStyle({
        colorVariant,
        containerStyle,
        selectStyle,
        optionStyle,
    });
    return {
        ...props,
        id,
        'aria-label': label,
        'aria-labelledby': labelledBy,
        ...selectProps,
        components: {
            MultiValue: MultiSelectValue,
            Option: MultiSelectOption,
            ValueContainer: MultiSelectValueContainer,
        },
        hideSelectedOptions: false,
        closeMenuOnSelect: false,
        blurInputOnSelect: false,
    };
}
/**
 * A simple, extensible MultiSelect component.
 */
export const MultiSelect = (props) => {
    const selectDefinition = useMultiSelect(props);
    return _jsx(BaseSelect, { ...selectDefinition, isMulti: true });
};
function useMultiSelectField({ fieldId, label, labelledBy, error, useDefaultPlaceholder, ...selectProps }) {
    return {
        fieldId,
        label,
        labelledBy,
        error,
        value: selectProps.value,
        initialValue: selectProps.defaultValue,
        // react-select's dropdown icon is 20px wide with 8px padding on all sides.
        rightPadding: '36px',
        useDefaultPlaceholder,
        renderInput: state => {
            const { onFocus, onUnfocus, setIsFilled, labelId, helpId, inputId, variant, } = state;
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const selectDefinition = useMultiSelect({
                ...selectProps,
                id: inputId,
                labelledBy: labelId,
                colorVariant: variant,
            });
            return (_jsx(BaseSelect, { ...selectDefinition, onMenuClose: onUnfocus, onMenuOpen: onFocus, "aria-describedby": helpId, onChange: option => {
                    selectProps.onChange?.(option);
                    setIsFilled(option != null);
                }, isMulti: true }));
        },
    };
}
/**
 * A form field containing a label and a `<Select>` component.
 * Supports all of the properties from `<Select>` and `<InputField>`
 */
export const MultiSelectField = (props) => {
    const inputFieldProps = useMultiSelectField(props);
    return _jsx(InputField, { ...inputFieldProps });
};
export { 
/**
 * @deprecated
 */
Select, 
/**
 * @deprecated
 */
SelectField, };
